import { Logger } from '_common/services';
import { NodeUtils } from 'Editor/services/DataManager';
import { JsonRange, SelectionFixer } from 'Editor/services/_Common/Selection';
import { Command } from '../../Command';
import { StylesUtils } from 'Editor/services/Edition_v2/Managers/Styles/StylesUtils';

export class RemoveStylesCommand extends Command {
  protected stylesToRemove: Editor.Edition.AggregatedStylesMap;
  protected options: Editor.Edition.StylesCommandOptions;

  protected rangeToApply?: Editor.Selection.JsonRange;

  constructor(
    context: Editor.Edition.Context,
    stylesToRemove: Editor.Edition.AggregatedStylesMap = {},
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context);

    this.stylesToRemove = stylesToRemove;
    this.options = {
      applySelection: true,
      createPatch: true,
      ...options,
    };

    this.rangeToApply = rangeToApply;
  }

  private async removeStylesFromBlockData(
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
  ) {
    if (!this.context.stylesManager || !this.context.contentManipulator) {
      return false;
    }

    const styles = Object.typedKeys(this.stylesToRemove);

    for (let i = 0; i < styles.length; i++) {
      const style = styles[i];
      if (StylesUtils.isAllowedBlockStyle(blockData.type, style)) {
        await this.context.contentManipulator.removeStyleFromBlock(baseModel, blockPath, style);
      }
    }

    if (this.options.resetParagraphStyle) {
      await this.context.contentManipulator.resetParagraphStyle(baseModel, blockData, blockPath);
    }
  }

  private removeStylesFromTextData(
    range: Editor.Selection.JsonRange,
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
  ) {
    const baseData = baseModel.selectedData();

    if (!this.context.stylesManager || !this.context.contentManipulator || !baseData) {
      return false;
    }

    let resultRange: Editor.Selection.JsonRange | undefined = range;

    const styles = Object.typedKeys(this.stylesToRemove);

    for (let i = 0; i < styles.length; i++) {
      const style = styles[i];
      if (StylesUtils.isAllowedInlineStyle(blockData.type, style)) {
        if (Array.isArray(this.stylesToRemove[style])) {
          for (let j = 0; j < this.stylesToRemove[style].length; j++) {
            const value = this.stylesToRemove[style][j];

            resultRange = this.context.contentManipulator.removeStyleFromContent(
              baseModel,
              resultRange,
              style,
              value,
            );
          }
        } else if (this.stylesToRemove[style] == null) {
          resultRange = this.context.contentManipulator.removeStyleFromContent(
            baseModel,
            resultRange,
            style,
          );
        }
      }

      range.updateRangePositions(resultRange.start, resultRange.end);
    }
  }

  private async removeStylesFromRange(ctx: Editor.Edition.ActionContext) {
    if (!this.context.DataManager || !this.context.stylesManager) {
      return false;
    }

    // normalize text selection
    SelectionFixer.normalizeTextSelection(
      ctx.range,
      {
        // suggestionMode: this.context.editionMode === 'SUGGESTIONS',
        forceTextAsWrap: true,
      },
      this.context.DataManager,
    );

    const blockRangesData = JsonRange.splitRangeByTypes(
      this.context.DataManager,
      ctx.range,
      [...StylesUtils.STYLABLE_BLOCKS],
      {
        sameContainerLevel: this.options.sameContainerLevel || false,
        useSelectedCells: this.options.useSelectedCells || true,
      },
    );

    for (let i = 0; i < blockRangesData.length; i++) {
      const range = blockRangesData[i].range;
      const blockData = blockRangesData[i].filteredData;
      const blockPath = blockRangesData[i].filteredDataPath;

      const baseModel = this.context.DataManager.nodes.getNodeModelById(range.start.b);

      // check if element is editable
      if (!baseModel || !this.context.DataManager.nodes.isNodeEditable(baseModel.id)) {
        continue;
      }

      let subStartPath: Editor.Selection.Path | undefined;
      if (range.start.p.length >= blockPath.length) {
        subStartPath = range.start.p.slice(blockPath.length);
      }

      let subEndPath: Editor.Selection.Path | undefined;
      if (range.end.p.length >= blockPath.length) {
        subEndPath = range.end.p.slice(blockPath.length);
      }

      if (subStartPath && subEndPath) {
        if (NodeUtils.isBlockTextData(blockData)) {
          if (
            NodeUtils.isPathAtContentStart(blockData, subStartPath) &&
            NodeUtils.isPathAtContentEnd(blockData, subEndPath)
          ) {
            await this.removeStylesFromBlockData(baseModel, blockData, blockPath);
          }
          this.removeStylesFromTextData(range, baseModel, blockData);
        } else {
          await this.removeStylesFromBlockData(baseModel, blockData, blockPath);
        }
      } else {
        // something is wrong
        Logger.captureException('ERROR: Invalid paths apply styles!!!');
      }

      // update range positions
      if (i === 0) {
        ctx.range.updateStartPosition(range.start);
      }

      if (i === blockRangesData.length - 1) {
        ctx.range.updateEndPosition(range.end);
      }
    }
  }

  protected async handleExec(...args: any[]) {
    if (this.context.debug) {
      Logger.trace('ApplyStyleCommand exec', this);
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext(this.rangeToApply);

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    // if (!this.actionContext.range.collapsed) {
    if (Object.keys(this.stylesToRemove).length > 0) {
      await this.removeStylesFromRange(this.actionContext);
    }

    if (this.options.useSelectedCells || this.options.collapseSelection === 'START') {
      this.actionContext.range.collapseToStart();
    }
    if (this.options.collapseSelection === 'END') {
      this.actionContext.range.collapseToEnd();
    }

    if (this.options.applySelection) {
      this.applySelection();
    }

    // force selection changed to update styles values
    // this.context.VisualizerManager?.selection.triggerSelectionChanged();

    if (this.options.createPatch) {
      this.createPatch();
    }
    // }
  }
}
